import type { DocumentRegistryEntry } from '@kennisbank/shared';

/**
 * Pure helpers behind the Documents page bulk actions (kb_admin only).
 *
 * runSequentially — applies one async action per item, one at a time, and
 *                   reports which items failed and why.
 * documentsToCsv  — metadata CSV for the "Export" action.
 */

export interface BulkFailure<T> {
  item: T;
  message: string;
}

export interface BulkResult<T> {
  succeeded: T[];
  failed: BulkFailure<T>[];
}

/**
 * Sequential on purpose: every admin document PATCH / DELETE takes the
 * shared `documents` ingest lock and answers 409 while it is held, so firing
 * the whole selection in parallel would make all but one request fail.
 * One failure never aborts the rest — the caller reports partial results.
 */
export async function runSequentially<T>(
  items: readonly T[],
  action: (item: T) => Promise<void>,
  onProgress?: (done: number, total: number) => void,
): Promise<BulkResult<T>> {
  const succeeded: T[] = [];
  const failed: BulkFailure<T>[] = [];
  for (const item of items) {
    try {
      await action(item);
      succeeded.push(item);
    } catch (err) {
      failed.push({ item, message: err instanceof Error ? err.message : 'Request failed.' });
    }
    onProgress?.(succeeded.length + failed.length, items.length);
  }
  return { succeeded, failed };
}

const CSV_COLUMNS: { header: string; value: (d: DocumentRegistryEntry) => string }[] = [
  { header: 'Title', value: (d) => d.documentTitle || d.filename },
  { header: 'Filename', value: (d) => d.filename },
  { header: 'Author', value: (d) => d.author ?? '' },
  { header: 'Type', value: (d) => d.documentType ?? '' },
  { header: 'Ring', value: (d) => d.ring ?? '' },
  { header: 'Classification', value: (d) => d.classification ?? 'internal' },
  { header: 'Access groups', value: (d) => (d.accessGroups ?? []).join('; ') },
  { header: 'Specialisations', value: (d) => (d.specialisations ?? []).join('; ') },
  { header: 'Document date', value: (d) => d.documentDate ?? '' },
  { header: 'Last indexed', value: (d) => d.lastIngestedAt ?? '' },
  { header: 'Chunks', value: (d) => String(d.chunkCount ?? '') },
  { header: 'Download only', value: (d) => (d.documentOnly ? 'yes' : '') },
];

function csvCell(raw: string): string {
  // Spreadsheet apps execute cells that start with = + - @ (CSV formula
  // injection). Titles and authors come from uploaded files, so neutralise
  // them with a leading apostrophe before quoting.
  const safe = /^[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export function documentsToCsv(docs: readonly DocumentRegistryEntry[]): string {
  const lines = [CSV_COLUMNS.map((c) => csvCell(c.header)).join(',')];
  for (const d of docs) lines.push(CSV_COLUMNS.map((c) => csvCell(c.value(d))).join(','));
  // CRLF + the BOM added at download time keep Excel happy with non-ASCII titles.
  return lines.join('\r\n');
}
